import { defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Col, Grid } from "@components/layout/Grid"
import { cn, tv } from "@utils/tw"
import { ChartContainer, Difference, Selector, Toggle, ToggleContainer } from "@components/atoms/data/Components"
import { getColor, getQuarters, getShortText, round } from "@utils/transportAffordabilityHelpers"
import { Typography } from "@components/atoms/Typography"
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { useMeasure } from "react-use"
import { Heading } from "@components/atoms/Heading"
import { VSpace } from "@components/layout/Spacer"
import { createGradient } from "@utils/createGradient"
import { useMethodologyTooltip, useTransportData } from "@hooks/queries"
import { numberWithCommas } from "@utils/numberWithCommas"
import { useBreakpointIsBelow } from "@hooks/useViewportDerived"
import { INFO } from "@components/atoms/Icons"
import { Tooltip } from "react-tooltip"

export const meta: BlockMeta = {
  title: "Transport Affordability over Time",
  category: "common",
  icon: "image-filter",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

ChartJS.register(...registerables)

type DataDetailT = {
  type: string
  cost: number
  quarter: string
}

type ChartDataT = {
  location: string
  state: string
  taxonomy: "City" | "Regional"
  data: DataDetailT[][]
}

type BubbleGroupT = {
  label: string
  quarter: string
  data: BubbleDataT[]
}

type BubbleDataT = {
  location: string
  state: string
  taxonomy: "City" | "Regional"
  cost: number
  percentage: number
  color: string
}

type LocationT = {
  location: string
  state: "NSW" | "VIC" | "National" | "QLD" | "SA" | "WA" | "TAS" | "NT" | "ACT"
  taxonomy: "City" | "Regional"
}

export default defineBlock("data/transport-affordability-over-time", (props) => {
  const transportAffordabilityData = useTransportData(undefined)
  const hydrating = useHydrating()
  const [location1, setLocation1] = useState<LocationT>({
    location: "Sydney",
    state: "NSW",
    taxonomy: "City",
  })
  const [location2, setLocation2] = useState<LocationT>({
    location: "Melbourne",
    state: "VIC",
    taxonomy: "City",
  })

  const [timeframe, setTimeframe] = useState<"weekly" | "yearly">("weekly")
  const rawChartData: ChartDataT[] = useMemo(() => {
    const sortedData: ChartDataT[] = []

    if (transportAffordabilityData.data?.transportAffordabilityData) {
      const datasets = transportAffordabilityData.data.transportAffordabilityData.map((data) =>
        JSON.parse(data?.data ?? ""),
      )
      const sortedQuarters = getQuarters(datasets)

      const locations: string[] = []
      datasets.forEach((dataset) => {
        if (!locations.includes(dataset["Location"])) {
          locations.push(dataset["Location"])
        }
      })

      locations.forEach((location) => {
        const dataByLocation = datasets.filter((dataset) => dataset["Location"] === location)
        const dataset: ChartDataT = {
          location: location,
          state: dataByLocation[0]["State"],
          taxonomy: dataByLocation[0]["City or Regional"],
          data: sortedQuarters.map((quarter) => {
            const locationData: DataDetailT[] = []
            dataByLocation.forEach((data, i) => {
              locationData.push({
                type: data["Field"],
                cost: parseFloat(dataByLocation[i][quarter].replace("$", "")),
                quarter: quarter,
              })
            })
            return locationData
          }),
        }

        sortedData.push(dataset)
      })
    }

    return sortedData
  }, [transportAffordabilityData.data?.transportAffordabilityData, timeframe])

  const [dataset, setDataset] = useState<BubbleGroupT[]>()
  const [total1, setTotal1] = useState<number>(0)
  const [total2, setTotal2] = useState<number>(0)
  const [currentQuarter, setCurrentQuarter] = useState<string>("")

  useEffect(() => {
    if (rawChartData.length > 0) {
      setCurrentQuarter(
        rawChartData.filter((data) => data.location === location1.location)[0].data.slice(-1)[0][0].quarter,
      )
    }
  }, [rawChartData])

  useEffect(() => {
    if (rawChartData.length > 0 && currentQuarter !== "") {
      const datasets: BubbleGroupT[] = []

      const locale1 = rawChartData.filter((data) => data.location === location1.location)[0]
      const locale2 = rawChartData.filter((data) => data.location === location2.location)[0]

      const totalCost1 = locale1.data[locale1.data.length - 1]
        .filter((i) => i.type !== "Share of Income")
        .map((i) => i.cost)
        .reduce((a, b) => a + b)
      const totalCost2 = locale2.data[locale2.data.length - 1]
        .filter((i) => i.type !== "Share of Income")
        .map((i) => i.cost)
        .reduce((a, b) => a + b)

      const floatCost1 = parseFloat(round(totalCost1, 2))
      const floatCost2 = parseFloat(round(totalCost2, 2))

      const floatCost1year = parseFloat(round(totalCost1 * 52, 2))
      const floatCost2year = parseFloat(round(totalCost2 * 52, 2))

      setTotal1(timeframe === "weekly" ? floatCost1 : floatCost1year)
      setTotal2(timeframe === "weekly" ? floatCost2 : floatCost2year)

      const locale1data = locale1.data
        .filter((dataset) => dataset[0].quarter === currentQuarter)[0]
        .sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))
      const locale2data = locale2.data
        .filter((dataset) => dataset[0].quarter === currentQuarter)[0]
        .sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))

      locale1data.forEach((ld, i) => {
        const datagroups: BubbleDataT[] = []
        datagroups.push(
          {
            location: locale1.location,
            state: locale1.state,
            taxonomy: locale1.taxonomy,
            cost: timeframe === "weekly" ? parseFloat(round(ld.cost, 2)) : parseFloat(round(ld.cost * 52, 2)),
            percentage: (ld.cost / totalCost1) * 100,
            color: getColor(locale1.state, locale1.taxonomy === "Regional" ? true : false),
          },
          {
            location: locale2.location,
            state: locale2.state,
            taxonomy: locale2.taxonomy,
            cost:
              timeframe === "weekly"
                ? parseFloat(round(locale2data[i].cost, 2))
                : parseFloat(round(locale2data[i].cost * 52, 2)),
            percentage: (locale2data[i].cost / totalCost2) * 100,
            color: getColor(locale2.state, locale2.taxonomy === "Regional" ? true : false),
          },
        )

        datasets.push({
          label: ld.type,
          quarter: ld.quarter,
          data: datagroups,
        })
      })

      setDataset(datasets.filter((data) => data.label !== "Share of Income"))
    }
  }, [rawChartData, location1, location2, currentQuarter, timeframe])

  return (
    <ChartContainer>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        <Col initial={12}>
          <ToggleContainer>
            <Toggle>
              <button className={timeframe === "weekly" ? "active" : ""} onClick={() => setTimeframe("weekly")}>
                Weekly
              </button>
              <button className={timeframe === "yearly" ? "active" : ""} onClick={() => setTimeframe("yearly")}>
                Yearly
              </button>
            </Toggle>
          </ToggleContainer>
        </Col>
        <Col initial={12} tablet={6}>
          {!hydrating && rawChartData && (
            <>
              <Selector region={location1.state} regional={location1.taxonomy === "Regional" ? true : false}>
                <label>Location</label>
                <div>
                  <span />
                  <select
                    value={JSON.stringify(location1)}
                    onChange={(e) => setLocation1(JSON.parse(e.currentTarget.value) as LocationT)}
                  >
                    {rawChartData.map((data, i) => (
                      <option
                        key={i}
                        value={JSON.stringify({
                          location: data.location,
                          state: data.state,
                          taxonomy: data.taxonomy,
                        })}
                      >
                        {data.location}
                      </option>
                    ))}
                  </select>
                </div>
              </Selector>

              <Table highlightColor={getColor(location1.state, location1.taxonomy === "Regional" ? true : false)}>
                <tbody>
                  <tr>
                    <td>
                      <TableDetail>
                        <label>Total transport costs</label>
                        <Typography
                          as="span"
                          variant="text-l"
                          style={{ color: getColor(location1.state, location1.taxonomy === "Regional" ? true : false) }}
                        >
                          ${numberWithCommas(total1)}
                        </Typography>

                        <Difference
                          current={total1}
                          previous={
                            rawChartData
                              .filter((data) => data.location === location1.location)[0]
                              ?.data.slice(-2, -1)[0]
                              .filter((type) => type.type !== "Share of Income")
                              .map((i) => i.cost)
                              .reduce((a, b) => a + b) * (timeframe === "yearly" ? 52 : 1)
                          }
                          previousQuarter={
                            rawChartData
                              .filter((data) => data.location === location1.location)[0]
                              ?.data.slice(-2, -1)[0][0].quarter
                          }
                          valueType="cost"
                          timeframe={timeframe}
                        />
                      </TableDetail>
                    </td>
                    <td>
                      <TableDetail>
                        <label>% of income*</label>
                        <Typography
                          as="span"
                          variant="text-l"
                          style={{ color: getColor(location1.state, location1.taxonomy === "Regional" ? true : false) }}
                        >
                          {rawChartData
                            .filter((data) => data.location === location1.location)[0]
                            ?.data.slice(-1)[0]
                            .filter((type) => type.type === "Share of Income")[0]
                            .cost.toFixed(1)}
                          %
                        </Typography>

                        <Difference
                          current={
                            rawChartData
                              .filter((data) => data.location === location1.location)[0]
                              ?.data.slice(-1)[0]
                              .filter((type) => type.type === "Share of Income")[0].cost
                          }
                          previous={
                            rawChartData
                              .filter((data) => data.location === location1.location)[0]
                              ?.data.slice(-2, -1)[0]
                              .filter((type) => type.type === "Share of Income")[0].cost
                          }
                          previousQuarter={
                            rawChartData
                              .filter((data) => data.location === location1.location)[0]
                              ?.data.slice(-2, -1)[0][0].quarter
                          }
                          valueType="income"
                          timeframe={timeframe}
                        />
                      </TableDetail>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          )}
        </Col>
        <Col initial={12} tablet={6}>
          {!hydrating && rawChartData && (
            <>
              <Selector region={location2.state} regional={location2.taxonomy === "Regional" ? true : false}>
                <label>Location</label>
                <div>
                  <span />
                  <select
                    value={JSON.stringify(location2)}
                    onChange={(e) => setLocation2(JSON.parse(e.currentTarget.value) as LocationT)}
                  >
                    {rawChartData.map((data, i) => (
                      <option
                        key={i}
                        value={JSON.stringify({
                          location: data.location,
                          state: data.state,
                          taxonomy: data.taxonomy,
                        })}
                      >
                        {data.location}
                      </option>
                    ))}
                  </select>
                </div>
              </Selector>

              <Table highlightColor={getColor(location2.state, location2.taxonomy === "Regional" ? true : false)}>
                <tbody>
                  <tr>
                    <td>
                      <TableDetail>
                        <label>Total transport costs</label>
                        <Typography
                          as="span"
                          variant="text-l"
                          style={{ color: getColor(location2.state, location2.taxonomy === "Regional" ? true : false) }}
                        >
                          ${numberWithCommas(total2)}
                        </Typography>

                        <Difference
                          current={total2}
                          previous={
                            rawChartData
                              .filter((data) => data.location === location2.location)[0]
                              ?.data.slice(-2, -1)[0]
                              .filter((type) => type.type !== "Share of Income")
                              .map((i) => i.cost)
                              .reduce((a, b) => a + b) * (timeframe === "yearly" ? 52 : 1)
                          }
                          previousQuarter={
                            rawChartData
                              .filter((data) => data.location === location2.location)[0]
                              ?.data.slice(-2, -1)[0][0].quarter
                          }
                          valueType="cost"
                          timeframe={timeframe}
                        />
                      </TableDetail>
                    </td>
                    <td>
                      <TableDetail>
                        <label>% of income*</label>
                        <Typography
                          as="span"
                          variant="text-l"
                          style={{ color: getColor(location2.state, location2.taxonomy === "Regional" ? true : false) }}
                        >
                          {rawChartData
                            .filter((data) => data.location === location2.location)[0]
                            ?.data.slice(-1)[0]
                            .filter((type) => type.type === "Share of Income")[0]
                            .cost.toFixed(1)}
                          %
                        </Typography>

                        <Difference
                          current={
                            rawChartData
                              .filter((data) => data.location === location2.location)[0]
                              ?.data.slice(-1)[0]
                              .filter((type) => type.type === "Share of Income")[0].cost
                          }
                          previous={
                            rawChartData
                              .filter((data) => data.location === location2.location)[0]
                              ?.data.slice(-2, -1)[0]
                              .filter((type) => type.type === "Share of Income")[0].cost
                          }
                          previousQuarter={
                            rawChartData
                              .filter((data) => data.location === location2.location)[0]
                              ?.data.slice(-2, -1)[0][0].quarter
                          }
                          valueType="income"
                          timeframe={timeframe}
                        />
                      </TableDetail>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          )}
        </Col>

        <Col initial={12}>
          <Heading as="h4">Cost breakdown by transport category</Heading>
          <VSpace initial={4} />
        </Col>

        <Col initial={12}>
          <ChartListContainer>
            {dataset && (
              <>
                <ChartList>
                  <TableKey>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th>
                            <span>&nbsp;</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <Span
                              style={{
                                color: getColor(
                                  dataset[0].data[0].state,
                                  dataset[0].data[0].taxonomy === "Regional" ? true : false,
                                ),
                              }}
                            >
                              {getShortText(dataset[0].data[0].location)}
                            </Span>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Span
                              style={{
                                color: getColor(
                                  dataset[0].data[1].state,
                                  dataset[0].data[1].taxonomy === "Regional" ? true : false,
                                ),
                              }}
                            >
                              {getShortText(dataset[0].data[1].location)}
                            </Span>
                          </td>
                        </tr>
                      </tbody>
                    </BreakdownTable>
                  </TableKey>

                  {/* Car loan Payments */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Car loan Payments")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Car loan Payments")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Car loan payments</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Car loan Payments")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Tolls */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Tolls")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Tolls")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Tolls</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Tolls")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Fuel */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Fuel")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Fuel")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Fuel</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Fuel")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Insurance */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Insurance")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Insurance")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Insurance</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Insurance")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Public Transport */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Public Transport")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Public Transport")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Public Transport</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Public Transport")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Registration, CTP & Licensing */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Registration, CTP & Licensing")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Registration, CTP & Licensing")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Registration, CTP & Licensing</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Registration, CTP & Licensing")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Servicing & Tyres */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Servicing & Tyres")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Servicing & Tyres")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Servicing & Tyres</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Servicing & Tyres")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>

                  {/* Roadside Assist */}
                  <VisualisationContainer
                    visIndex={dataset
                      ?.sort((a, b) => {
                        const cost1 = b.data[0].cost + b.data[1].cost
                        const cost2 = a.data[0].cost + a.data[1].cost
                        return cost1 - cost2
                      })
                      .map((e) => e.label)
                      .indexOf("Roadside Assist")}
                  >
                    <Dots>
                      {dataset
                        .filter((data) => data.label === "Roadside Assist")[0]
                        .data.map((locale, k) => (
                          <Dot
                            key={k}
                            style={{
                              background: locale.color as string,
                              width: `${locale.percentage * 2}px`,
                              height: `${locale.percentage * 2}px`,
                            }}
                          />
                        ))}
                    </Dots>
                    <BreakdownTable>
                      <thead>
                        <tr>
                          <th colSpan={2}>
                            <span>Roadside Assist</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset
                          ?.filter((data) => data.label === "Roadside Assist")[0]
                          .data.map((locale, k) => (
                            <tr key={k}>
                              <td>${numberWithCommas(parseFloat(round(locale.cost, 2)), true)}</td>
                              <td>{locale.percentage.toFixed(1)}%</td>
                            </tr>
                          ))}
                      </tbody>
                    </BreakdownTable>
                  </VisualisationContainer>
                </ChartList>
              </>
            )}
          </ChartListContainer>
        </Col>

        <Col initial={12}>
          {!hydrating && rawChartData && (
            <ChartArea
              data={rawChartData}
              location1={location1}
              location2={location2}
              setCurrentQuarter={setCurrentQuarter}
              timeframe={timeframe}
            />
          )}
        </Col>

        <Col initial={12}>
          <VSpace initial={7} />
          <Typography variant="text-xs" className="text-right">
            <sup>*</sup> Typical household income for the location.
          </Typography>
        </Col>
      </div>
    </ChartContainer>
  )
})

const tableStyles = tv({
  base: [
    "w-full my-7 border-collapse table-fixed",
    "[&_tr>th]:p-3 [&_tr>th]:border [&_tr>th]:border-grey",
    "[&_tr>td]:p-3 [&_tr>td]:border [&_tr>td]:border-grey [&_tr>td]:align-top [&_tr>td]:relative",
    "[&_tr>td::before]:content-[''] [&_tr>td::before]:absolute [&_tr>td::before]:inset-0 [&_tr>td::before]:opacity-[0.05] [&_tr>td::before]:z-0 [&_tr>td::before]:bg-[var(--ta-highlight)]",
  ],
})

const Table = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<"table"> & { highlightColor?: string }>(
  ({ highlightColor, style, className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn(tableStyles(), className)}
      style={{ ...style, ["--ta-highlight" as any]: highlightColor }}
      {...props}
    />
  ),
)
Table.displayName = "Table"

const tableDetailStyles = tv({
  base: [
    "flex flex-col items-center relative z-[1]",
    "[&_label]:type-body-xs [&_label]:!font-extrabold [&_label]:!leading-[1.25]",
    "[&>span]:my-3 [&>span]:font-extrabold",
    "[&>div]:type-body-xs [&>div]:text-black",
  ],
})

const TableDetail = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(tableDetailStyles(), className)} {...props} />
))
TableDetail.displayName = "TableDetail"

// `var(--tw-grid-inner-width)` is the tailwind-helper grid var equivalent of
// stitches' `$grid$inner$width` — see components/atoms/data/Components.tsx.
// `calc(100vw - (100vw - X))` always resolves to `X`, so it's simplified
// directly to the grid var rather than transcribed literally.
const ChartListContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("w-[var(--tw-grid-inner-width)] overflow-auto pb-4 relative", className)} {...props} />
  ),
)
ChartListContainer.displayName = "ChartListContainer"

const ChartList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex w-[calc((160px*8)+40px)] lg:w-full xl:w-[1720px]", className)} {...props} />
))
ChartList.displayName = "ChartList"

const TableKey = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-[40px] pt-[160px] sticky top-0 left-0 bg-bg z-[2]",
      "lg:pt-[calc((var(--tw-grid-inner-width)-40px)*(1/8))]",
      "xl:pt-[calc((1720px-40px)*(1/8))]",
      className,
    )}
    {...props}
  />
))
TableKey.displayName = "TableKey"

// `visIndex` carries the (already-sorted) position of this category among
// the 8 breakdown categories — computed once by the caller instead of once
// per breakpoint (as the original per-breakpoint stitches `css` values did),
// since it's the same number at every breakpoint. Exposed as a CSS custom
// property so the responsive `left`/`width` calc()s (which need it inside a
// static Tailwind arbitrary value) can read it.
const visualisationContainerStyles = tv({
  base: [
    "flex flex-col w-[160px] absolute transition-all duration-[350ms] ease-movement",
    "left-[calc(160px*var(--vis-index)+40px)]",
    "lg:w-[calc((var(--tw-grid-inner-width)-40px)*(1/8))] lg:left-[calc((var(--tw-grid-inner-width)-40px)*(1/8)*var(--vis-index)+40px)]",
    "xl:w-[calc((1720px-40px)*(1/8))] xl:left-[calc((1720px-40px)*(1/8)*var(--vis-index)+40px)]",
  ],
})

const VisualisationContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { visIndex?: number }>(
  ({ visIndex, style, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(visualisationContainerStyles(), className)}
      style={{ ...style, ["--vis-index" as any]: visIndex }}
      {...props}
    />
  ),
)
VisualisationContainer.displayName = "VisualisationContainer"

const Dots = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("block w-full h-0 pt-[100%] relative", className)} {...props} />
))
Dots.displayName = "Dots"

const dotStyles = tv({
  base: [
    "rounded-full overflow-hidden absolute top-1/2 left-1/2 transition-all duration-[350ms] ease-movement",
    "[&:nth-of-type(1)]:[transform:translate(-80%,-50%)]",
    "[&:nth-of-type(2)]:[transform:translate(-20%,-50%)]",
  ],
})

const Dot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(dotStyles(), className)} {...props} />
))
Dot.displayName = "Dot"

const breakdownTableStyles = tv({
  base: [
    "w-full max-w-full border-collapse",
    "[&_thead_tr_th]:border-b [&_thead_tr_th]:border-b-grey [&_thead_tr_th]:h-[50px]",
    "[&_thead_tr_th_span]:type-body-xs [&_thead_tr_th_span]:!font-extrabold [&_thead_tr_th_span]:!leading-[1.25] [&_thead_tr_th_span]:inline-flex [&_thead_tr_th_span]:w-full [&_thead_tr_th_span]:h-[36px] [&_thead_tr_th_span]:items-center [&_thead_tr_th_span]:justify-center",
    "[&_tbody_tr_td]:type-body-xs [&_tbody_tr_td]:border-b [&_tbody_tr_td]:border-b-grey [&_tbody_tr_td]:py-1 [&_tbody_tr_td]:text-center",
    "[&_tbody_tr_td:first-of-type]:!font-extrabold",
  ],
})

const BreakdownTable = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<"table">>(
  ({ className, ...props }, ref) => <table ref={ref} className={cn(breakdownTableStyles(), className)} {...props} />,
)
BreakdownTable.displayName = "BreakdownTable"

const Span = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("inline-block w-full text-left", className)} {...props} />
))
Span.displayName = "Span"

const ChartArea = ({
  data,
  location1,
  location2,
  setCurrentQuarter,
  timeframe,
}: {
  data: ChartDataT[]
  location1: LocationT
  location2: LocationT
  setCurrentQuarter: React.Dispatch<React.SetStateAction<string>>
  timeframe: "weekly" | "yearly"
}) => {
  const isMobile = useBreakpointIsBelow("tablet")
  const chartRef = useRef<ChartJS>(null)
  const [instanceRef, instance] = useMeasure<HTMLDivElement>()

  const options: ChartOptions = {
    aspectRatio: instance.width / 400,

    scales: {
      y: {
        beginAtZero: true,

        ticks: {
          callback: (value) => {
            return `$${numberWithCommas(value as number)}`
          },
        },
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
        },

        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value as number)
            if (label.includes("Q1") || index === 0) {
              const split = label.split(" ")
              if (isMobile) return split[1]
              return [split[0], split[1]]
            }
            if (isMobile) return ""
            return label.split(" ")[0]
          },
        },
      },
    },

    onHover: (event, element, chart) => {
      if (element !== undefined && element.length > 0) {
        setCurrentQuarter(data[0].data[element[0].index][0].quarter)

        let x = element[0].element.x
        let yAxis = chart.scales.y
        let ctx = chart.ctx
        ctx.save()
        ctx.globalCompositeOperation = "destination-over"
        ctx.beginPath()
        ctx.setLineDash([5, 3])
        ctx.moveTo(x, yAxis.top)
        ctx.lineTo(x, yAxis.bottom)
        ctx.lineWidth = 1.2
        ctx.strokeStyle = "rgba(206, 206, 206, 1)"
        ctx.stroke()
        ctx.restore()
      }
    },

    interaction: {
      mode: "index",
      intersect: false,
    },

    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 0,
        caretSize: 0,
        caretPadding: 20,
        padding: 16,
        boxPadding: 5,
        usePointStyle: true,

        itemSort: function (a, b, data) {
          return parseFloat(b.formattedValue) - parseFloat(a.formattedValue)
        },

        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label === "Capital city avg." ? "Capital Avg." : ctx.dataset.label
            const value = numberWithCommas(parseFloat(round(ctx.raw as number, 2)), true)
            return `${label} $${value}`
          },
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.borderColor,
              backgroundColor: ctx.element.options.borderColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        display: false,
      },
    },
  }

  const chartData: ChartData<"line"> = useMemo(() => {
    if (data && location1 && location2) {
      const data1 = data.filter((location) => location.location === location1.location)[0]
      const data2 = data.filter((location) => location.location === location2.location)[0]
      const labels = data1?.data.map((dataset) => dataset[0].quarter)

      const average = () => {
        const returnData: number[] = []
        const quarterAverages: {
          quarter: string
          data: number[]
        }[] = []
        const allData = data.filter((location) => location.taxonomy !== "Regional").map((location) => location.data)
        const quarters = allData[0]?.map((dataset) => dataset[0].quarter)

        quarters?.forEach((quarter, i) => {
          quarterAverages.push({
            quarter: quarter,
            data: [],
          })

          allData?.forEach((location) => {
            location?.forEach((q, k) => {
              if (quarterAverages[k]?.quarter === quarter)
                quarterAverages[k].data.push(
                  q
                    .filter((i) => i.type !== "Share of Income")
                    .map((i) => (timeframe === "weekly" ? i.cost : i.cost * 52))
                    .reduce((a, b) => a + b),
                )
            })
          })
        })

        quarterAverages.forEach((ave) => {
          returnData.push(parseFloat((ave.data.reduce((a, b) => a + b) / ave.data.length).toFixed(2)))
        })

        return returnData
      }

      const getBg = ({ dataset }: { dataset: ChartDataT }) => {
        if (chartRef.current) {
          const chart = chartRef.current
          return createGradient(
            chart.ctx,
            chart.chartArea,
            getColor(dataset?.state, dataset?.taxonomy === "Regional" ? true : false)
              .replace("rgb(", "rgba(")
              .replace(")", ", 1)"),
            300,
            0.1,
          )
        }

        return getColor(dataset?.state, dataset?.taxonomy === "Regional" ? true : false)
          .replace("rgb(", "rgba(")
          .replace(")", ", .05)")
      }

      return {
        labels: labels,
        datasets: [
          {
            label: data1?.location,
            pointBackgroundColor: getColor(data1?.state, data1?.taxonomy === "Regional" ? true : false),
            pointRadius: 0,
            borderColor: getColor(data1?.state, data1?.taxonomy === "Regional" ? true : false),
            borderWidth: 2,
            tension: 0.2,
            backgroundColor: getBg({ dataset: data1 }),
            fill: true,
            data: data1?.data.map((dataset) =>
              dataset
                .filter((i) => i.type !== "Share of Income")
                .map((i) => (timeframe === "weekly" ? i.cost : i.cost * 52))
                .reduce((a, b) => a + b),
            ),
          },
          {
            label: data2?.location,
            pointBackgroundColor: getColor(data2?.state, data2?.taxonomy === "Regional" ? true : false),
            pointRadius: 0,
            borderColor: getColor(data2?.state, data2?.taxonomy === "Regional" ? true : false),
            borderWidth: 2,
            tension: 0.2,
            backgroundColor: getBg({ dataset: data2 }),
            fill: true,
            data: data2?.data.map((dataset) =>
              dataset
                .filter((i) => i.type !== "Share of Income")
                .map((i) => (timeframe === "weekly" ? i.cost : i.cost * 52))
                .reduce((a, b) => a + b),
            ),
          },
          {
            label: "Capital city avg.",
            pointBackgroundColor: getColor("NATIONAL", false),
            pointRadius: 0,
            borderColor: getColor("NATIONAL", false),
            borderWidth: 2,
            tension: 0.2,
            borderDash: [6, 3],
            fill: false,
            data: average(),
          },
        ],
      }
    }
    return { datasets: [] }
  }, [data, location1, location2, chartRef.current, timeframe])

  const taTooltip = useMethodologyTooltip(undefined)
  const [tooltipPosition, setTooltipPosition] = useState<number | null>(null)
  const afterdraw = {
    id: "vertical_line",
    afterDraw: (chart: any, args: any, options: any) => {
      const { ctx } = chart
      const q32024 = chart.scales.x.ticks
        .filter((tick: any) => tick.label instanceof Array)
        .filter((tick: any) => tick.label.includes("2024"))

      if (q32024.length > 0) {
        const position = q32024[0].value + 2
        const xPos = chart.scales.x._gridLineItems[position].tx1
        const yPos = chart?.scales?.x?.top ?? null
        const boxTop = chart?.scales?.y?.paddingTop ?? null
        setTooltipPosition(xPos)

        if (xPos && yPos && boxTop) {
          ctx.save()
          ctx.beginPath()
          ctx.setLineDash([5, 3])
          ctx.moveTo(xPos, 0)
          ctx.lineTo(xPos, yPos)
          ctx.lineWidth = 1.2
          ctx.strokeStyle = "rgba(0, 0, 0, 1)"
          ctx.stroke()
          ctx.restore()
        }
      }
    },
  }

  return (
    <ChartInstance ref={instanceRef} tooltipLeft={tooltipPosition}>
      {tooltipPosition && taTooltip.data?.siteSettings?.themeSiteSettings?.transportAffordabilityTooltip && (
        <i>
          <a className="tt-methodology-overtime">{INFO}</a>
          <Tooltip
            anchorSelect=".tt-methodology-overtime"
            style={{
              width: "250px",
              zIndex: 50,
              fontFamily: "$body",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.43,
            }}
          >
            {taTooltip.data.siteSettings.themeSiteSettings.transportAffordabilityTooltip}
          </Tooltip>
        </i>
      )}
      <Chart
        type="line"
        ref={chartRef}
        data={chartData}
        options={options}
        onMouseLeave={() =>
          setCurrentQuarter(data.filter((data) => data.location === location1.location)[0].data.slice(-1)[0][0].quarter)
        }
        plugins={[afterdraw]}
      />
    </ChartInstance>
  )
}

// `tooltipLeft` positions the methodology tooltip icon (rendered as a
// descendant `<i>`) at the x-position of the "Q3 2024" chart tick, computed
// dynamically in the afterDraw plugin above — exposed as a CSS custom
// property since it targets a descendant selector, not the root element.
const chartInstanceStyles = tv({
  base: [
    "w-full h-[400px] relative mt-7",
    "[&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:!w-full",
    "[&_i]:absolute [&_i]:m-0 [&_i]:z-10 [&_i]:left-[var(--tooltip-left)] [&_i]:[transform:translate(-50%,-100%)]",
  ],
})

const ChartInstance = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { tooltipLeft?: number | null }>(
  ({ tooltipLeft, style, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(chartInstanceStyles(), className)}
      style={{ ...style, ["--tooltip-left" as any]: `${tooltipLeft ?? 0}px` }}
      {...props}
    />
  ),
)
ChartInstance.displayName = "ChartInstance"
